"use client";

import { useMemo, useState } from "react";
import { Gauge, Table2 } from "lucide-react";

import { Card } from "@/components/ui-system";
import { useElementWidth } from "@/lib/hooks/use-element-width";
import type { DeviceChannelDto, TelemetryAggregatePointDto, TelemetryDto } from "@/lib/api/types";
import { TelemetryUPlotChart } from "@/modules/telemetry/components/telemetry-uplot-chart";
import type { GraphType } from "@/modules/core/datalogger-create/schema/datalogger.schema";
import { formatRelativeTime, getChannelDisplayLabel } from "@/modules/telemetry/page-utils";
import {
  telemetrySeverityTextClass,
  telemetryThresholdSeverity,
  type ChannelThresholds,
} from "@/modules/telemetry/thresholds";
import { cn } from "@/lib/utils/cn";

export type LiveChannelTileProps = {
  deviceId: string;
  channel: DeviceChannelDto;
  aggregatePoints: TelemetryAggregatePointDto[];
  rawRows: TelemetryDto[];
  dataRevision: number;
  latestValue: unknown;
  updatedAt: string | null;
  thresholds: ChannelThresholds;
  graphType: GraphType;
};

type ChannelSample = {
  time: string;
  timestamp: number;
  value: number;
};

type OhlcBucket = {
  start: number;
  end: number;
  open: number;
  high: number;
  low: number;
  close: number;
};

type GanttInterval = {
  start: number;
  end: number;
  state: "Normal" | "Warning" | "Critical";
  value: number;
};

type HistogramBin = {
  min: number;
  max: number;
  count: number;
  start: number | null;
  end: number | null;
};

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatRawTelemetryValue(value: unknown): string {
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "--";
  }
  if (typeof value === "boolean") {
    return value ? "True" : "False";
  }
  if (typeof value === "string" && value.trim()) {
    return value;
  }
  return "--";
}

function buildChannelSamples(
  channelKey: string,
  rawRows: TelemetryDto[],
  aggregatePoints: TelemetryAggregatePointDto[],
): ChannelSample[] {
  const rawSamples = rawRows
    .map((row) => {
      const timestamp = new Date(row.time).getTime();
      const value = coerceNumber(row.data?.[channelKey]);
      return Number.isFinite(timestamp) && value != null
        ? { time: row.time, timestamp, value }
        : null;
    })
    .filter((sample): sample is ChannelSample => sample != null)
    .sort((a, b) => a.timestamp - b.timestamp);

  if (rawSamples.length > 0) {
    return rawSamples;
  }

  return aggregatePoints
    .map((point) => {
      const timestamp = new Date(point.t).getTime();
      const value = coerceNumber(point.values[channelKey]);
      return Number.isFinite(timestamp) && value != null
        ? { time: point.t, timestamp, value }
        : null;
    })
    .filter((sample): sample is ChannelSample => sample != null)
    .sort((a, b) => a.timestamp - b.timestamp);
}

function buildOhlcBuckets(samples: ChannelSample[], maxBuckets = 18): OhlcBucket[] {
  if (samples.length === 0) {
    return [];
  }
  if (samples.length === 1) {
    const only = samples[0]!;
    return [{
      start: only.timestamp,
      end: only.timestamp,
      open: only.value,
      high: only.value,
      low: only.value,
      close: only.value,
    }];
  }

  const first = samples[0]!.timestamp;
  const last = samples[samples.length - 1]!.timestamp;
  const bucketMs = Math.max(1, Math.ceil((last - first + 1) / maxBuckets));
  const buckets = new Map<number, ChannelSample[]>();

  samples.forEach((sample) => {
    const index = Math.floor((sample.timestamp - first) / bucketMs);
    const current = buckets.get(index) ?? [];
    current.push(sample);
    buckets.set(index, current);
  });

  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, bucketSamples]) => {
      const ordered = bucketSamples.sort((a, b) => a.timestamp - b.timestamp);
      const values = ordered.map((sample) => sample.value);
      return {
        start: first + index * bucketMs,
        end: first + (index + 1) * bucketMs,
        open: ordered[0]!.value,
        high: Math.max(...values),
        low: Math.min(...values),
        close: ordered[ordered.length - 1]!.value,
      };
    });
}

function buildGanttIntervals(samples: ChannelSample[], thresholds: ChannelThresholds): GanttInterval[] {
  if (samples.length === 0) {
    return [];
  }

  const intervals: GanttInterval[] = [];
  samples.forEach((sample, index) => {
    const next = samples[index + 1];
    const severity = telemetryThresholdSeverity(sample.value, thresholds);
    const state: GanttInterval["state"] =
      severity === "critical" ? "Critical" : severity === "warning" ? "Warning" : "Normal";
    const start = sample.timestamp;
    const end = next?.timestamp ?? sample.timestamp + Math.max(60_000, sample.timestamp - (samples[index - 1]?.timestamp ?? sample.timestamp));
    const previous = intervals[intervals.length - 1];
    if (previous && previous.state === state && previous.end >= start) {
      previous.end = end;
      return;
    }
    intervals.push({ start, end, state, value: sample.value });
  });
  return intervals;
}

function buildHistogramBins(samples: ChannelSample[], maxBins = 12): HistogramBin[] {
  const values = samples.map((sample) => sample.value).filter(Number.isFinite);
  if (values.length === 0) {
    return [];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max <= min) {
    const timestamps = samples.map((sample) => sample.timestamp).filter(Number.isFinite);
    return [{
      min,
      max,
      count: values.length,
      start: timestamps.length > 0 ? Math.min(...timestamps) : null,
      end: timestamps.length > 0 ? Math.max(...timestamps) : null,
    }];
  }

  const binCount = Math.max(3, Math.min(maxBins, Math.ceil(Math.sqrt(values.length))));
  const size = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, (_, index) => ({
    min: min + index * size,
    max: index === binCount - 1 ? max : min + (index + 1) * size,
    count: 0,
    start: null as number | null,
    end: null as number | null,
  }));

  samples.forEach((sample) => {
    const index = Math.min(binCount - 1, Math.floor((sample.value - min) / size));
    const bin = bins[index]!;
    bin.count += 1;
    bin.start = bin.start == null ? sample.timestamp : Math.min(bin.start, sample.timestamp);
    bin.end = bin.end == null ? sample.timestamp : Math.max(bin.end, sample.timestamp);
  });

  return bins;
}

function formatChartTime(value: number): string {
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatChartRange(start: number, end: number): string {
  if (Math.abs(end - start) < 1000) {
    return formatChartTime(start);
  }
  return `${formatChartTime(start)} - ${formatChartTime(end)}`;
}

function scaleValue(value: number, min: number, max: number, height: number, padding: number): number {
  if (max <= min) {
    return height / 2;
  }
  return padding + ((max - value) / (max - min)) * (height - padding * 2);
}

function OhlcSeriesView({ buckets, mode, unit }: { buckets: OhlcBucket[]; mode: "candlestick" | "ohlc"; unit?: string | null }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const width = 640;
  const height = 220;
  const padding = 28;
  const values = buckets.flatMap((bucket) => [bucket.low, bucket.high]);
  const min = values.length > 0 ? Math.min(...values) : 0;
  const max = values.length > 0 ? Math.max(...values) : 1;
  const slot = buckets.length > 0 ? (width - padding * 2) / buckets.length : 0;
  const activeBucket = buckets[activeIndex ?? buckets.length - 1] ?? null;

  if (buckets.length === 0) {
    return (
      <div className="flex h-[var(--live-chart-height)] items-center justify-center rounded-control border border-dashed border-card-border text-caption text-muted">
        Waiting for samples
      </div>
    );
  }

  return (
    <div className="space-y-ds-3">
      <svg className="h-[var(--live-chart-canvas-height)] w-full" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={mode === "candlestick" ? "Candlestick chart" : "OHLC chart"}>
        <line x1={padding} x2={width - padding} y1={height - padding} y2={height - padding} className="stroke-border" strokeWidth="1" />
        <line x1={padding} x2={padding} y1={padding} y2={height - padding} className="stroke-border" strokeWidth="1" />
        <text x={padding + 4} y={padding - 8} className="fill-muted text-micro font-semibold">{formatRawTelemetryValue(max)}</text>
        <text x={padding + 4} y={height - padding - 6} className="fill-muted text-micro font-semibold">{formatRawTelemetryValue(min)}</text>
        {buckets.map((bucket, index) => {
          const x = padding + slot * index + slot / 2;
          const yHigh = scaleValue(bucket.high, min, max, height, padding);
          const yLow = scaleValue(bucket.low, min, max, height, padding);
          const yOpen = scaleValue(bucket.open, min, max, height, padding);
          const yClose = scaleValue(bucket.close, min, max, height, padding);
          const rising = bucket.close >= bucket.open;
          const color = rising ? "var(--semantic-success)" : "var(--semantic-error)";
          const bodyWidth = Math.max(7, Math.min(22, slot * 0.5));
          const bodyY = Math.min(yOpen, yClose);
          const bodyHeight = Math.max(3, Math.abs(yClose - yOpen));
          const isActive = activeIndex === index;

          return (
            <g
              key={`${bucket.start}-${index}`}
              className="cursor-pointer outline-none"
              tabIndex={0}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
            >
              <rect x={padding + slot * index} y={padding} width={Math.max(4, slot)} height={height - padding * 2} fill="transparent" />
              <line x1={x} x2={x} y1={yHigh} y2={yLow} stroke={color} strokeWidth={isActive ? "3" : "2"} strokeLinecap="round" />
              {mode === "candlestick" ? (
                <rect
                  x={x - bodyWidth / 2}
                  y={bodyY}
                  width={bodyWidth}
                  height={bodyHeight}
                  rx="2"
                  fill={rising ? "color-mix(in srgb, var(--semantic-success) 72%, transparent)" : "color-mix(in srgb, var(--semantic-error) 72%, transparent)"}
                  stroke={color}
                  strokeWidth={isActive ? "2.5" : "1.5"}
                />
              ) : (
                <>
                  <line x1={x - bodyWidth / 1.5} x2={x} y1={yOpen} y2={yOpen} stroke={color} strokeWidth={isActive ? "3.5" : "2.5"} strokeLinecap="round" />
                  <line x1={x} x2={x + bodyWidth / 1.5} y1={yClose} y2={yClose} stroke={color} strokeWidth={isActive ? "3.5" : "2.5"} strokeLinecap="round" />
                </>
              )}
              {isActive ? <circle cx={x} cy={yClose} r="4" fill={color} /> : null}
            </g>
          );
        })}
      </svg>
      {activeBucket ? (
        <div className="rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2">
          <div className="flex flex-wrap items-center justify-between gap-ds-2 text-caption">
            <span className="font-semibold text-foreground">{formatChartRange(activeBucket.start, activeBucket.end)}</span>
            <span className="text-muted">{unit || "value"}</span>
          </div>
          <div className="mt-ds-2 grid grid-cols-4 gap-ds-2 text-caption">
            {[
              ["Open", activeBucket.open],
              ["High", activeBucket.high],
              ["Low", activeBucket.low],
              ["Close", activeBucket.close],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0">
                <p className="dl-label text-muted/80">{label}</p>
                <p className="truncate font-semibold text-foreground">{formatRawTelemetryValue(value)}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function GanttStateView({ intervals }: { intervals: GanttInterval[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  if (intervals.length === 0) {
    return (
      <div className="flex h-[var(--live-chart-height)] items-center justify-center rounded-control border border-dashed border-card-border text-caption text-muted">
        Waiting for state intervals
      </div>
    );
  }

  const start = intervals[0]!.start;
  const end = Math.max(...intervals.map((interval) => interval.end));
  const span = Math.max(1, end - start);
  const colorByState: Record<GanttInterval["state"], string> = {
    Normal: "var(--semantic-success)",
    Warning: "var(--semantic-warning)",
    Critical: "var(--semantic-error)",
  };
  const activeInterval = intervals[activeIndex ?? intervals.length - 1] ?? null;

  return (
    <div className="rounded-control border border-card-border bg-surface-secondary p-ds-4">
      <div className="relative mt-ds-4 h-16 rounded-control bg-surface-inset">
        {intervals.map((interval, index) => {
          const left = ((interval.start - start) / span) * 100;
          const width = Math.max(1.5, ((interval.end - interval.start) / span) * 100);
          const isActive = activeIndex === index;
          return (
            <button
              key={`${interval.start}-${index}`}
              type="button"
              className="absolute top-4 h-8 rounded-md outline-none transition-[filter,box-shadow] focus-visible:ring-2 focus-visible:ring-ring"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                background: colorByState[interval.state],
                filter: isActive ? "brightness(1.08)" : undefined,
                boxShadow: isActive ? "0 0 0 2px color-mix(in srgb, var(--bg-surface) 92%, transparent), 0 0 0 4px var(--border-strong)" : undefined,
              }}
              title={`${interval.state} - ${formatChartRange(interval.start, interval.end)}`}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
            />
          );
        })}
      </div>
      <div className="mt-ds-5 flex flex-wrap gap-ds-3 text-caption text-muted">
        {(["Normal", "Warning", "Critical"] as const).map((state) => (
          <span key={state} className="inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorByState[state] }} />
            {state}
          </span>
        ))}
      </div>
      {activeInterval ? (
        <div className="mt-ds-3 rounded-control border border-card-border bg-surface-primary px-ds-3 py-ds-2">
          <div className="flex flex-wrap items-center justify-between gap-ds-2 text-caption">
            <span className="font-semibold text-foreground">{activeInterval.state}</span>
            <span className="text-muted">{formatChartRange(activeInterval.start, activeInterval.end)}</span>
          </div>
          <div className="mt-ds-1 text-caption text-muted">
            Reading at state change: <span className="font-semibold text-foreground">{formatRawTelemetryValue(activeInterval.value)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function HistogramView({ bins, unit }: { bins: HistogramBin[]; unit?: string | null }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const width = 640;
  const height = 220;
  const padding = 28;
  const maxCount = Math.max(1, ...bins.map((bin) => bin.count));
  const slot = bins.length > 0 ? (width - padding * 2) / bins.length : 0;
  const activeBin = activeIndex == null ? null : bins[activeIndex] ?? null;
  const totalCount = bins.reduce((sum, bin) => sum + bin.count, 0);
  const histogramStart = bins
    .map((bin) => bin.start)
    .filter((value): value is number => value != null);
  const histogramEnd = bins
    .map((bin) => bin.end)
    .filter((value): value is number => value != null);
  const summaryStart = histogramStart.length > 0 ? Math.min(...histogramStart) : null;
  const summaryEnd = histogramEnd.length > 0 ? Math.max(...histogramEnd) : null;
  const valueMin = Math.min(...bins.map((bin) => bin.min));
  const valueMax = Math.max(...bins.map((bin) => bin.max));
  const readoutMin = activeBin?.min ?? valueMin;
  const readoutMax = activeBin?.max ?? valueMax;
  const readoutCount = activeBin?.count ?? totalCount;
  const readoutStart = activeBin?.start ?? summaryStart;
  const readoutEnd = activeBin?.end ?? summaryEnd;

  if (bins.length === 0) {
    return (
      <div className="flex h-[var(--live-chart-height)] items-center justify-center rounded-control border border-dashed border-card-border text-caption text-muted">
        Waiting for samples
      </div>
    );
  }

  return (
    <div className="space-y-ds-3">
      <svg
        className="h-[var(--live-chart-canvas-height)] w-full"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Histogram chart"
        onMouseLeave={() => setActiveIndex(null)}
      >
        <line x1={padding} x2={width - padding} y1={height - padding} y2={height - padding} className="stroke-border" strokeWidth="1" />
        <line x1={padding} x2={padding} y1={padding} y2={height - padding} className="stroke-border" strokeWidth="1" />
        <text x={padding + 4} y={padding - 8} className="fill-muted text-micro font-semibold">{maxCount} samples</text>
        {bins.map((bin, index) => {
          const barWidth = Math.max(8, slot * 0.72);
          const barHeight = (bin.count / maxCount) * (height - padding * 2);
          const x = padding + slot * index + (slot - barWidth) / 2;
          const y = height - padding - barHeight;
          const isActive = activeIndex === index;

          return (
            <g
              key={`${bin.min}-${bin.max}-${index}`}
              className="cursor-pointer outline-none"
              tabIndex={0}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
            >
              <rect x={padding + slot * index} y={padding} width={Math.max(4, slot)} height={height - padding * 2} fill="transparent" />
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(2, barHeight)}
                rx="3"
                fill={isActive ? "var(--accent)" : "color-mix(in srgb, var(--accent) 72%, transparent)"}
              />
            </g>
          );
        })}
      </svg>
      <div className="rounded-control border border-card-border bg-surface-secondary px-ds-3 py-ds-2">
        <div className="flex flex-wrap items-center justify-between gap-ds-2 text-caption">
          <span className="font-semibold text-foreground">
            {formatRawTelemetryValue(readoutMin)} - {formatRawTelemetryValue(readoutMax)} {unit || ""}
          </span>
          <span className="text-muted">{readoutCount} sample{readoutCount === 1 ? "" : "s"}</span>
        </div>
        <div className="mt-ds-1 text-caption text-muted">
          {readoutStart != null && readoutEnd != null
            ? `Period: ${formatChartRange(readoutStart, readoutEnd)}`
            : "No samples in this bucket."}
        </div>
      </div>
    </div>
  );
}

export function LiveChannelTile({
  deviceId,
  channel,
  aggregatePoints,
  rawRows,
  dataRevision,
  latestValue,
  thresholds,
  graphType,
}: LiveChannelTileProps) {
  const chart = useElementWidth<HTMLDivElement>(320);
  const hasEnvelope = thresholds.min !== null || thresholds.max !== null;
  const latestSeverity = telemetryThresholdSeverity(latestValue, thresholds);
  const numericLatestValue = typeof latestValue === "number" ? latestValue : typeof latestValue === "string" ? Number(latestValue) : NaN;
  const hasThresholdConfiguration =
    thresholds.min !== null ||
    thresholds.max !== null ||
    thresholds.warningLow !== null ||
    thresholds.warningHigh !== null;
  const hasLatestReading = Number.isFinite(numericLatestValue);
  const channelSamples = useMemo(
    () => buildChannelSamples(channel.key, rawRows, aggregatePoints),
    [aggregatePoints, channel.key, rawRows],
  );
  const ohlcBuckets = useMemo(() => buildOhlcBuckets(channelSamples), [channelSamples]);
  const ganttIntervals = useMemo(() => buildGanttIntervals(channelSamples, thresholds), [channelSamples, thresholds]);
  const histogramBins = useMemo(() => buildHistogramBins(channelSamples), [channelSamples]);
  const liveSeries = aggregatePoints
    .map((point) => ({
      time: point.t,
      value: point.values[channel.key],
    }))
    .filter((point) => typeof point.value === "number" && Number.isFinite(point.value));
  const recentRows = liveSeries.slice(-6).reverse();
  const gaugeMin = channel.min_value ?? thresholds.min ?? 0;
  const gaugeMax = channel.max_value ?? thresholds.max ?? 100;
  const gaugeRange = gaugeMax > gaugeMin ? gaugeMax - gaugeMin : 1;
  const gaugeRatio = Number.isFinite(numericLatestValue)
    ? Math.max(0, Math.min(1, (numericLatestValue - gaugeMin) / gaugeRange))
    : 0;
  const supportedGraphType =
    graphType === "area" ||
    graphType === "bar" ||
    graphType === "step" ||
    graphType === "scatter" ||
    graphType === "gauge" ||
    graphType === "histogram" ||
    graphType === "candlestick" ||
    graphType === "ohlc" ||
    graphType === "gantt" ||
    graphType === "table"
      ? graphType
      : "line";
  const uplotGraphType =
    supportedGraphType === "area" ||
    supportedGraphType === "bar" ||
    supportedGraphType === "step" ||
    supportedGraphType === "scatter"
      ? supportedGraphType
      : "line";

  return (
    <Card
      className={cn(
        "w-full min-w-0 space-y-ds-3 overflow-hidden p-[var(--density-panel-padding)]",
        latestSeverity === "critical"
          ? "border-status-error-border bg-status-error-surface"
          : latestSeverity === "warning"
            ? "border-status-warning-border bg-status-warning-surface"
            : hasThresholdConfiguration && hasLatestReading
              ? "border-status-success-border bg-status-success-surface"
              : "border-card-border bg-surface-primary",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-ds-4 gap-y-ds-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-ds-2 gap-y-1">
          <p className="truncate text-body font-semibold leading-tight text-foreground">{getChannelDisplayLabel(channel)}</p>
          {hasEnvelope ? (
            <span className="inline-flex max-w-full items-center rounded-full border border-card-border bg-surface-secondary px-ds-2 py-0.5 text-caption text-muted">
              Expected {thresholds.min ?? "?"} to {thresholds.max ?? "?"} {channel.unit || ""}
            </span>
          ) : null}
        </div>
        <div className="shrink-0">
          <p
            className={cn(
              "text-display font-bold leading-none tracking-tight",
              latestSeverity ? telemetrySeverityTextClass(latestSeverity) : "text-foreground",
            )}
          >
            {formatRawTelemetryValue(latestValue)}
            {channel.unit ? <span className="ml-1 text-title font-semibold">{channel.unit}</span> : null}
          </p>
        </div>
      </div>

      {supportedGraphType === "gauge" ? (
        <div className="rounded-control bg-surface-inset px-ds-4 py-ds-4">
          <div className="flex items-center justify-between gap-ds-3">
            <div className="flex items-center gap-ds-2 text-caption text-muted">
              <Gauge className="h-4 w-4" aria-hidden />
              Live gauge
            </div>
            <div className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">
              {formatRawTelemetryValue(gaugeMin)} to {formatRawTelemetryValue(gaugeMax)}
            </div>
          </div>
          <div className="mx-auto mt-ds-4 flex w-full max-w-[13rem] flex-col items-center gap-ds-3">
            <div
              className="relative h-40 w-40 rounded-full"
              style={{
                background: `conic-gradient(var(--accent) 0deg ${Math.round(gaugeRatio * 270)}deg, color-mix(in srgb, var(--bg-surface-muted) 76%, var(--border)) ${Math.round(gaugeRatio * 270)}deg 270deg, transparent 270deg 360deg)`,
              }}
            >
              <div className="absolute inset-[1.15rem] rounded-full bg-card" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className={cn("text-display-sm font-semibold", latestSeverity ? telemetrySeverityTextClass(latestSeverity) : "text-foreground")}>
                  {formatRawTelemetryValue(latestValue)}
                </p>
                <p className="text-caption text-muted">{channel.unit || "Live value"}</p>
              </div>
            </div>
          </div>
        </div>
      ) : supportedGraphType === "candlestick" || supportedGraphType === "ohlc" ? (
        <div className="w-full min-w-0 overflow-hidden rounded-control bg-surface-inset px-ds-2 py-ds-3">
          <OhlcSeriesView buckets={ohlcBuckets} mode={supportedGraphType} unit={channel.unit} />
        </div>
      ) : supportedGraphType === "histogram" ? (
        <div className="w-full min-w-0 overflow-hidden rounded-control bg-surface-inset px-ds-2 py-ds-3">
          <HistogramView bins={histogramBins} unit={channel.unit} />
        </div>
      ) : supportedGraphType === "gantt" ? (
        <div className="w-full min-w-0 overflow-hidden rounded-control bg-surface-inset px-ds-2 py-ds-3">
          <GanttStateView intervals={ganttIntervals} />
        </div>
      ) : supportedGraphType === "table" ? (
        <div className="overflow-hidden rounded-control bg-surface-inset">
          <div className="flex items-center gap-ds-2 border-b border-border/60 px-ds-4 py-ds-3 text-caption text-muted">
            <Table2 className="h-4 w-4" aria-hidden />
            Recent samples
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[16rem] text-left text-caption">
              <thead className="border-b border-border/50 text-micro uppercase tracking-[0.12em] text-muted/90">
                <tr>
                  <th className="px-ds-4 py-ds-2">Time</th>
                  <th className="px-ds-4 py-ds-2">Value</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.length > 0 ? (
                  recentRows.map((row) => (
                    <tr key={row.time} className="border-b border-border/40 last:border-b-0">
                      <td className="px-ds-4 py-ds-2 text-muted">{formatRelativeTime(row.time)}</td>
                      <td className="px-ds-4 py-ds-2 font-medium text-foreground">{formatRawTelemetryValue(row.value)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="px-ds-4 py-ds-4 text-muted">
                      Waiting for samples
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div ref={chart.ref} className="w-full min-w-0 overflow-hidden rounded-control border border-slate-300 bg-[#f8fafc] px-ds-2 py-ds-2 dark:border-slate-600 dark:bg-[#101827]">
          <TelemetryUPlotChart
            width={chart.width}
            height={220}
            deviceId={deviceId}
            metricKeys={[channel.key]}
            seriesLabels={{ [channel.key]: getChannelDisplayLabel(channel) }}
            aggregatePoints={aggregatePoints}
            dataRevision={dataRevision}
            liveEnabled={false}
            liveWindowPoints={512}
            chartStyle={uplotGraphType}
          />
        </div>
      )}
    </Card>
  );
}
